import React, { useEffect, useRef, useState } from "react";
import type { Project } from "../../types/redux";

export interface MenuAnchor {
  top: number;
  left: number;
}

/** Captured so the delete dialog survives `currentProject` being cleared. */
export interface DeleteTarget {
  id: string;
  name: string;
}

export interface HeaderMenus {
  isProjectModalOpen: boolean;
  openProjectModal: () => void;
  closeProjectModal: () => void;
  isHeaderMenuOpen: boolean;
  headerMenuPosition: MenuAnchor;
  toggleHeaderMenu: (event: React.MouseEvent<HTMLElement>) => void;
  closeHeaderMenu: () => void;
  isRenameModalOpen: boolean;
  startRename: () => void;
  closeRenameModal: () => void;
  deleteTarget: DeleteTarget | null;
  startDelete: () => void;
  clearDeleteTarget: () => void;
}

function targetFor(project: Project | null): DeleteTarget | null {
  if (!project?.id) return null;
  return { id: project.id, name: project.name || `Project ${project.id.slice(0, 8)}` };
}

/**
 * Every menu/dialog the header can open, plus the transitions between them
 * (opening rename or delete always closes whatever launched it).
 */
export function useHeaderMenus(project: Project | null): HeaderMenus {
  const [isProjectModalOpen, setIsProjectModalOpen] = useState<boolean>(false);
  const [isHeaderMenuOpen, setIsHeaderMenuOpen] = useState<boolean>(false);
  const [headerMenuPosition, setHeaderMenuPosition] = useState<MenuAnchor>({ top: 0, left: 0 });
  const [isRenameModalOpen, setIsRenameModalOpen] = useState<boolean>(false);
  const [deleteTarget, setDeleteTarget] = useState<DeleteTarget | null>(null);

  // Close the header context menu when the active project changes
  useEffect(() => {
    setIsHeaderMenuOpen(false);
  }, [project?.id]);

  const closeLaunchers = (): void => {
    setIsHeaderMenuOpen(false);
    setIsProjectModalOpen(false);
  };

  return {
    isProjectModalOpen,
    openProjectModal: () => setIsProjectModalOpen(true),
    closeProjectModal: () => setIsProjectModalOpen(false),
    isHeaderMenuOpen,
    headerMenuPosition,
    toggleHeaderMenu: (event) => {
      const rect = event.currentTarget.getBoundingClientRect();
      setHeaderMenuPosition({ top: rect.bottom + 4, left: rect.left });
      setIsHeaderMenuOpen((open) => !open);
    },
    closeHeaderMenu: () => setIsHeaderMenuOpen(false),
    isRenameModalOpen,
    startRename: () => {
      closeLaunchers();
      setIsRenameModalOpen(true);
    },
    closeRenameModal: () => setIsRenameModalOpen(false),
    deleteTarget,
    startDelete: () => {
      closeLaunchers();
      setDeleteTarget(targetFor(project));
    },
    clearDeleteTarget: () => setDeleteTarget(null),
  };
}

/** Bumped whenever the project name changes so the name button re-runs its fade-in. */
export function useProjectNameKey(name: string | null | undefined): number {
  const [projectNameKey, setProjectNameKey] = useState<number>(0);
  const prevProjectNameRef = useRef<string | null | undefined>(name);

  useEffect(() => {
    if (prevProjectNameRef.current !== name && name) {
      setProjectNameKey((prev) => prev + 1);
      prevProjectNameRef.current = name;
    }
  }, [name]);

  return projectNameKey;
}
